'use client';

/**
 * Safe wrapper around PostHog that returns null when posthog-js is not
 * installed. This allows the standalone @bazak/bazak-assistant package to
 * work without posthog-js as a dependency — consumers that want analytics
 * can install posthog-js themselves and wrap the app with PostHogProvider.
 *
 * The PostHog instance is bridged through our own React context (set by
 * PostHogProvider) so we never need to import posthog-js directly here.
 */

import { createContext, useContext } from 'react';

export type PostHogLike = {
  register: (properties: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  captureException: (error: unknown, properties?: Record<string, unknown>) => void;
  startSessionRecording: () => void;
  stopSessionRecording: () => void;
  get_distinct_id: () => string;
  alias: (alias: string, distinctId?: string) => void;
  /**
   * Optional because a consumer without posthog-js has no flags — which reads as "no variant", the
   * safe answer everywhere this is used. Calling it is also what records the exposure PostHog's
   * experiment analysis counts, so it belongs at the point the guest could see the difference.
   */
  getFeatureFlag?: (key: string) => string | boolean | undefined;
  /** Fires once flags have arrived from the network, so a first render without them can re-read. */
  onFeatureFlags?: (callback: () => void) => (() => void) | void;
};

const SafePostHogContext = createContext<PostHogLike | null>(null);

export const SafePostHogProvider = SafePostHogContext.Provider;

/**
 * Drop-in replacement for `usePostHog()` that gracefully returns null when
 * posthog-js is not available in the consumer's node_modules.
 */
export function useSafePostHog(): PostHogLike | null {
  return useContext(SafePostHogContext);
}
